import { useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Icon from 'react-bootstrap-icons';

import { useResumenPaciente } from './hooks/useResumenPaciente';
import { usePaciente } from './hooks/usePaciente';
import { useDatosGenerales } from './hooks/useDatosGenerales';
import { useCitaConfirmar } from './hooks/useCitaConfirmar';
import { useCitaCancelar } from './hooks/useCitaCancelar';
import { useEnlazarApp } from './hooks/useEnlazarApp';
import { useDesenlazarApp } from './hooks/useDesenlazarApp';

import { Acordion, Empty, Error, Forbidden, LightButton, Loader, ModalContainer, PrimaryButton, ProgressSteps, TextInputPreview } from '../../components';
import { AsociadoPreview } from './components/AsociadoPreview';

import styles from './PacienteView.module.css';
import { IndependizarPacienteModal } from '../Modals/IndependizarPaciente';

export async function loader({ params }) {
    return params.id;
}

export const PacienteView = () => {

    const navigate = useNavigate();

    const id = useLoaderData();

    const { permisos } = useSelector(state => state.auth);

    const { resumenPacienteQuery: { isLoading: isLoadingResumenPaciente, isError: isErrorResumenPaciente, data: resumenPaciente } } = useResumenPaciente(id);

    const { pacienteQuery: { isLoading: isLoadingPaciente, isError: isErrorPaciente, data: paciente } } = usePaciente(id);

    const { datosGeneralesQuery: { isLoading: isLoadingConfiguracionDatosGenerales, isError: isErrorConfiguracionDatosGenerales, data: configuracionDatosGenerales } } = useDatosGenerales();

    const { confirmarCitaMutation: { mutate: confirmarCita } } = useCitaConfirmar({ id });
    
    const { cancelarCitaMutation: { mutate: cancelarCita } } = useCitaCancelar(id);

    const { enlazarAppMutation: { mutate: enlazarApp } } = useEnlazarApp(id);

    const { desenlazarAppMutation: { mutate: desenlazarApp } } = useDesenlazarApp(id);

    const [statusIndependizarPacienteModal, setStatusIndependizarPacienteModal] = useState(false);

    const [pacienteAsociado, setPacienteAsociado] = useState({});

    const handleIndependizar = (asociado) => {
        setPacienteAsociado(asociado);
        setStatusIndependizarPacienteModal(true);
    }
    
    const hideCancelarIndependizar = () => {
        setStatusIndependizarPacienteModal(false);
        setPacienteAsociado({});
    }

    const handleCrearPacienteAsociado = () => {
        navigate(`/paciente/nuevo/${paciente._id}`);
    }

    const handleConfirmarCita = (id) => {
        confirmarCita({ id: id });
    }

    const handleCancelarCita = (id) => {
        cancelarCita({ id: id });
    }

    const handleEnlazarApp = (id) => {
        enlazarApp({ id: id });
    }

    const handleDesenlazarApp = (id) => {
        desenlazarApp({ id: id });
    }

    return (permisos.pacienteRead || permisos.pacienteUpdate || permisos.pacienteDelete || permisos.app) ? (
        <>
        {(isLoadingResumenPaciente || isLoadingPaciente || isLoadingConfiguracionDatosGenerales)
            ? <Loader />
            : <>
            {(isErrorResumenPaciente || isErrorPaciente || isErrorConfiguracionDatosGenerales)
                ? <Error />
                : <>
                    <div className="row">
                        <div className="col">
                            <div className={styles.panelSection + " " + styles.panelNoHeader}>
                                <div className="row">
                                    <div className="col-md-12 col-lg-8">
                                        <div className="row">
                                            {(permisos.citaRead || permisos.citaUpdate) &&
                                            <div className="col-md-12 col-lg-3">
                                                <div className={styles.panel}>
                                                    <div className="row">
                                                        <div className="col">
                                                            <div className={styles.resumenHeader}>Próxima cita</div>
                                                            <TextInputPreview
                                                                label=""
                                                                name="proximaCita"
                                                                value={(typeof resumenPaciente.proximaCita !== 'undefined' && resumenPaciente.proximaCita !== null) ? <div className={styles.resumenText}>{ `${ resumenPaciente.proximaCita.dia } ${ resumenPaciente.proximaCita.hora }` }<span className='ms-2'><Icon.Clock color={resumenPaciente.proximaCita.etiquetaColor} size={18} /></span></div> : '-'}
                                                            />
                                                        </div>
                                                    </div>
                                                    {(permisos.citaUpdate && typeof resumenPaciente.proximaCita !== 'undefined' && resumenPaciente.proximaCita !== null) &&
                                                    <>
                                                        <div className="row">
                                                            <div className="col">
                                                                <LightButton
                                                                    block={true}
                                                                    marginTop={false}
                                                                    onClick={() => handleCancelarCita(resumenPaciente.proximaCita._id)}
                                                                    text="Cancelar"
                                                                    type="button"
                                                                />
                                                            </div>
                                                            {(!resumenPaciente.proximaCita.confirmada) &&
                                                            <div className="col">
                                                                <PrimaryButton
                                                                    block={true}
                                                                    inverse={true}
                                                                    marginTop={false}
                                                                    onClick={() => handleConfirmarCita(resumenPaciente.proximaCita._id)}
                                                                    text="Confirmar"
                                                                    type="button"
                                                                />
                                                            </div>
                                                            }
                                                        </div>
                                                    </>}
                                                </div>
                                            </div>
                                            }
                                            {(permisos.inmunoterapiaRead) &&
                                            <div className="col-md-12 col-lg-3">
                                                <div className={styles.panel}>
                                                    <div className={styles.resumenHeader}>Inmunoterapia actual</div>
                                                    <TextInputPreview
                                                        label=""
                                                        name="inmunoterapiaActual"
                                                        value={(typeof resumenPaciente.inmunoterapiaActual !== 'undefined' && resumenPaciente.inmunoterapiaActual !== null) ? <div className={styles.resumenText}>
                                                            {(typeof resumenPaciente.inmunoterapiaActual.tipo !== 'undefined') &&
                                                            <div>{ resumenPaciente.inmunoterapiaActual.tipo }</div>
                                                            }
                                                            {(typeof resumenPaciente.inmunoterapiaActual.fase !== 'undefined') &&
                                                            <div>{ `${ resumenPaciente.inmunoterapiaActual.fase } frasco #${ resumenPaciente.inmunoterapiaActual.identificador }` }</div>
                                                            }
                                                        </div> : '-'}
                                                    />
                                                    <TextInputPreview
                                                        label="Última aplicación"
                                                        name="ultimaAplicacion"
                                                        value={(typeof resumenPaciente.inmunoterapiaActual !== 'undefined' && resumenPaciente.inmunoterapiaActual !== null) ? <div className={styles.resumenText}>
                                                            <div>{ (typeof resumenPaciente.inmunoterapiaActual.aplicacion !== 'undefined' && resumenPaciente.inmunoterapiaActual.aplicacion !== null && typeof resumenPaciente.inmunoterapiaActual.aplicacion.dosis !== 'undefined') ? `${ resumenPaciente.inmunoterapiaActual.aplicacion.dosis } ${ resumenPaciente.inmunoterapiaActual.tipoDosis } - ${ resumenPaciente.inmunoterapiaActual.aplicacion.fechaFormato }` : '' }</div>
                                                        </div> : '-'}
                                                    />
                                                </div>
                                            </div>
                                            }
                                            {(permisos.app) &&
                                            <div className="col-md-12 col-lg-6">
                                                <div className={styles.panel}>
                                                    <div className="row">
                                                        <div className="col">
                                                            <div className={styles.resumenHeader}>App Móvil</div>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col">
                                                            <TextInputPreview
                                                                label=""
                                                                name="appMovil"
                                                                value={<div>
                                                                    <div>
                                                                        <ProgressSteps
                                                                            status={parseInt(resumenPaciente.appMovil.appStatus)}
                                                                            steps={[
                                                                                { color: (parseInt(resumenPaciente.appMovil.appStatus) > 0) ? '#7FAE1B' : '#6C757D' }, // Punto 1
                                                                                { color: (parseInt(resumenPaciente.appMovil.appStatus) > 0) ? '#7FAE1B' : '#6C757D' }, // Línea 1-2
                                                                                { color: (parseInt(resumenPaciente.appMovil.appStatus) === 0) ? '#6C757D' : (parseInt(resumenPaciente.appMovil.appStatus) === 1) ? '#FEC600' : '#7FAE1B' }, // Punto 2
                                                                                { color: (parseInt(resumenPaciente.appMovil.appStatus) <= 1) ? '#6C757D' : '#7FAE1B' },    // Línea 2-3 (apagada)
                                                                                { color: (parseInt(resumenPaciente.appMovil.appStatus) <= 1) ? '#6C757D' : (parseInt(resumenPaciente.appMovil.appStatus) === 3) ? '#7FAE1B' : '#F34336' },    // Punto 3
                                                                            ]}
                                                                        />
                                                                    </div>
                                                                </div>}
                                                            />
                                                        </div>
                                                    </div>
                                                    {([0, 2].includes(parseInt(resumenPaciente.appMovil.appStatus))) &&
                                                    <div className="row">
                                                        <div className="col">
                                                            <PrimaryButton
                                                                block={true}
                                                                inverse={true}
                                                                marginTop={false}
                                                                onClick={() => handleEnlazarApp(id)}
                                                                text="Enlazar app"
                                                                type="button"
                                                            />
                                                        </div>
                                                    </div>
                                                    }
                                                    {([3].includes(parseInt(resumenPaciente.appMovil.appStatus))) &&
                                                    <div className="row">
                                                        <div className="col">
                                                            <PrimaryButton
                                                                block={true}
                                                                inverse={true}
                                                                marginTop={false}
                                                                onClick={() => handleDesenlazarApp(id)}
                                                                text="Desenlazar app"
                                                                type="button"
                                                            />
                                                        </div>
                                                    </div>
                                                    }
                                                </div>
                                            </div>
                                            }
                                        </div>
                                    </div>
                                    <div className="col-md-12 col-lg-4">
                                        <div className={styles.panelDivider}>
                                            <div className="row h-100">
                                                <div className="col h-100">
                                                    {(permisos.pacienteRead) &&
                                                    <>
                                                    <div className={styles.panelHeader}>Usuario Principal</div>
                                                    <div className="row">
                                                        <div className="col">
                                                            <TextInputPreview
                                                                label="Nombre"
                                                                name="nombre"
                                                                value={ paciente.usuario.nombre }
                                                            />
                                                        </div>
                                                        <div className="col">
                                                            <TextInputPreview
                                                                label="Correo electrónico"
                                                                name="email"
                                                                value={ paciente.usuario.email }
                                                            />
                                                        </div>
                                                    </div>
                                                    </>
                                                    }
                                                    {((permisos.pacienteRead || permisos.pacienteCreate || permisos.pacienteUpdate) && typeof paciente.pacientesAsociados !== 'undefined') && <>
                                                        <div className={styles.panelHeader}>Dependientes</div>
                                                        { (paciente.pacientesAsociados.length > 0) ? paciente.pacientesAsociados.map(asociado => <div key={asociado._id}>
                                                            <AsociadoPreview
                                                                asociado={asociado}
                                                                independizar={() => handleIndependizar(asociado)}
                                                            />
                                                        </div>) : <>
                                                            {(permisos.pacienteCreate) 
                                                                ? <div className='d-flex justify-content-start'>
                                                                    <PrimaryButton
                                                                        icon="Plus"
                                                                        inverse={true}
                                                                        marginTop={false}
                                                                        onClick={handleCrearPacienteAsociado}
                                                                        text="Agregar Paciente"
                                                                        type="button"
                                                                    />
                                                                </div>
                                                                : <Empty />
                                                            }
                                                        </> }
                                                    </>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {(permisos.pacienteRead) &&
                            <>
                            <div className={styles.panelSection + " " + styles.panelNoHeader}>
                                <div className={styles.subHeader}>Datos Generales</div>
                                <div className="row">
                                    <div className="col-md-12 col-lg-3">
                                        <TextInputPreview
                                            label="Paciente"
                                            name="paciente"
                                            value={ [(configuracionDatosGenerales.fichaIdentificacion.nombre && typeof paciente.nombre !== 'undefined') ? paciente.nombre : null, (configuracionDatosGenerales.fichaIdentificacion.apellidoPaterno && typeof paciente.apellidoPaterno !== 'undefined') ? paciente.apellidoPaterno : null, (configuracionDatosGenerales.fichaIdentificacion.apellidoMaterno && paciente.apellidoMaterno !== '' && paciente.apellidoMaterno !== undefined) ? paciente.apellidoMaterno : null].join(" ") }
                                        />
                                    </div>
                                    <div className="col-md-4 col-lg-3">
                                        <TextInputPreview
                                            label="Fecha de nacimiento"
                                            name="fechaNacimiento"
                                            value={ (typeof paciente.fechaNacimientoFormato !== 'undefined') ? paciente.fechaNacimientoFormato : '' }
                                        />
                                    </div>
                                    <div className="col-md-4 col-lg-3">
                                        <TextInputPreview
                                            label="Edad"
                                            name="edad"
                                            value={ (typeof paciente.edad !== 'undefined') ? paciente.edad : '' }
                                        />
                                    </div>
                                    <div className="col-md-4 col-lg-3">
                                        <TextInputPreview
                                            label="Género"
                                            name="genero"
                                            value={ (typeof paciente.genero !== 'undefined') ? paciente.genero : '' }
                                        />
                                    </div>
                                    <div className="col-md-12 col-lg-3">
                                        <TextInputPreview
                                            label="Correo electrónico"
                                            name="email"
                                            value={ (typeof paciente.email !== 'undefined') ? paciente.email : '' }
                                        />
                                    </div>
                                    {(configuracionDatosGenerales.fichaIdentificacion.telefono) &&
                                    <div className="col-md-4 col-lg-3">
                                        <TextInputPreview
                                            label="Teléfono fijo"
                                            name="telefono"
                                            value={ (paciente.telefono !== '' && paciente.telefono !== undefined && paciente.telefono !== null) ? paciente.telefono : '-' }
                                        />
                                    </div>
                                    }
                                    <div className="col-md-4 col-lg-3">
                                        <TextInputPreview
                                            label="Celular"
                                            name="celular"
                                            value={ (typeof paciente.celular !== 'undefined') ? paciente.celular : '' }
                                        />
                                    </div>
                                    {(configuracionDatosGenerales.fichaIdentificacion.medioContacto) &&
                                    <div className="col-md-4 col-lg-3">
                                        <TextInputPreview
                                            label="Medio de contacto"
                                            name="medioContacto"
                                            value={ (typeof paciente.medioContacto !== 'undefined') ? paciente.medioContacto : '' }
                                        />
                                    </div>
                                    }
                                    {/* <div className="col-md-6 col-lg-3">
                                        <TextInputPreview
                                            label="Diagnóstico(s)"
                                            name="diagnosticos"
                                            value="-"
                                        />
                                    </div> */}
                                    {/* <div className="col-md-6 col-lg-3">
                                        <TextInputPreview
                                            label="Última consulta(s)"
                                            name="ultimaConsulta"
                                            value="-"
                                        />
                                    </div> */}
                                </div>
                            </div>
                            <div className={styles.panelSection}>
                                <div className={styles.subHeader}>Domicilio</div>
                                <div className="row">
                                    {(configuracionDatosGenerales.domicilio.calle) &&
                                    <div className="col-md-12 col-lg-3">
                                        <TextInputPreview
                                            label="Calle"
                                            name="calle"
                                            value={ (paciente.calle !== '' && paciente.calle !== undefined && paciente.calle !== null) ? paciente.calle : '-' }
                                        />
                                    </div>
                                    }
                                    {(configuracionDatosGenerales.domicilio.numero) &&
                                    <div className="col-md-6 col-lg-3">
                                        <TextInputPreview
                                            label="# Exterior"
                                            name="numeroExterior"
                                            value={ (paciente.numeroExterior !== '' && paciente.numeroExterior !== undefined && paciente.numeroExterior !== null) ? paciente.numeroExterior : '-' }
                                        />
                                    </div>
                                    }
                                    {(configuracionDatosGenerales.domicilio.numero) &&
                                    <div className="col-md-6 col-lg-3">
                                        <TextInputPreview
                                            label="# Interior"
                                            name="numeroInterior"
                                            value={ (paciente.numeroInterior !== '' && paciente.numeroInterior !== undefined && paciente.numeroInterior !== null) ? paciente.numeroInterior : '-' }
                                        />
                                    </div>
                                    }
                                    {(configuracionDatosGenerales.domicilio.colonia) &&
                                    <div className="col-md-12 col-lg-3">
                                        <TextInputPreview
                                            label="Colonia"
                                            name="colonia"
                                            value={ (paciente.colonia !== '' && paciente.colonia !== undefined && paciente.colonia !== null) ? paciente.colonia : '-' }
                                        />
                                    </div>
                                    }
                                    {(configuracionDatosGenerales.domicilio.municipio) &&
                                    <div className="col-md-6 col-lg-3">
                                        <TextInputPreview
                                            label="Municipio"
                                            name="municipio"
                                            value={ (paciente.municipio !== '' && paciente.municipio !== undefined && paciente.municipio !== null) ? paciente.municipio : '-' }
                                        />
                                    </div>
                                    }
                                    {(configuracionDatosGenerales.domicilio.entidadFederativa) &&
                                    <div className="col-md-6 col-lg-3">
                                        <TextInputPreview
                                            label="Entidad federativa"
                                            name="entidadFederativa"
                                            value={ (paciente.entidadFederativa !== '' && paciente.entidadFederativa !== undefined && paciente.entidadFederativa !== null) ? paciente.entidadFederativa : '-' }
                                        />
                                    </div>
                                    }
                                    {(configuracionDatosGenerales.domicilio.pais) &&
                                    <div className="col-md-6 col-lg-3">
                                        <TextInputPreview
                                            label="País"
                                            name="pais"
                                            value={ (paciente.pais !== '' && paciente.pais !== undefined && paciente.pais !== null) ? paciente.pais : '-' }
                                        />
                                    </div>
                                    }
                                    {(configuracionDatosGenerales.domicilio.codigoPostal) &&
                                    <div className="col-md-6 col-lg-3">
                                        <TextInputPreview
                                            label="Código postal"
                                            name="codigoPostal"
                                            value={ (paciente.codigoPostal !== '' && paciente.codigoPostal !== undefined && paciente.codigoPostal !== null) ? paciente.codigoPostal : '-' }
                                        />
                                    </div>
                                    }
                                </div>
                            </div>
                            <div className={styles.panelSection}>
                                <div className="row">
                                    <div className="col">
                                        <Acordion
                                            body={<>
                                                <div className="row">
                                                    {(configuracionDatosGenerales.fichaIdentificacion.nacionalidad) &&
                                                    <div className="col-md-6 col-lg-2">
                                                        <TextInputPreview
                                                            label="Nacionalidad"
                                                            name="nacionalidad"
                                                            value={ (paciente.nacionalidad !== '' && paciente.nacionalidad !== undefined && paciente.nacionalidad !== null) ? paciente.nacionalidad : '-' }
                                                        />
                                                    </div>
                                                    }
                                                    {(configuracionDatosGenerales.fichaIdentificacion.estadoCivil) &&
                                                    <div className="col-md-6 col-lg-2">
                                                        <TextInputPreview
                                                            label="Estado civil"
                                                            name="estadoCivil"
                                                            value={ (paciente.estadoCivil !== '' && paciente.estadoCivil !== undefined && paciente.estadoCivil !== null) ? paciente.estadoCivil : '-' }
                                                        />
                                                    </div>
                                                    }
                                                    {(configuracionDatosGenerales.fichaIdentificacion.ocupacion) &&
                                                    <div className="col-md-6 col-lg-2">
                                                        <TextInputPreview
                                                            label="Ocupación"
                                                            name="ocupacion"
                                                            value={ (paciente.ocupacion !== '' && paciente.ocupacion !== undefined && paciente.ocupacion !== null) ? paciente.ocupacion : '-' }
                                                        />
                                                    </div>
                                                    }
                                                    {(configuracionDatosGenerales.fichaIdentificacion.religion) &&
                                                    <div className="col-md-6 col-lg-2">
                                                        <TextInputPreview
                                                            label="Religión"
                                                            name="religion"
                                                            value={ (paciente.religion !== '' && paciente.religion !== undefined && paciente.religion !== null) ? paciente.religion : '-' }
                                                        />
                                                    </div>
                                                    }
                                                    {(configuracionDatosGenerales.fichaIdentificacion.origen) &&
                                                    <div className="col-md-6 col-lg-2">
                                                        <TextInputPreview
                                                            label="Origen"
                                                            name="origen"
                                                            value={ (paciente.origen !== '' && paciente.origen !== undefined && paciente.origen !== null) ? paciente.origen : '-' }
                                                        />
                                                    </div>
                                                    }
                                                    {(configuracionDatosGenerales.fichaIdentificacion.residencia) &&
                                                    <div className="col-md-6 col-lg-2">
                                                        <TextInputPreview
                                                            label="Residencia"
                                                            name="residencia"
                                                            value={ (paciente.residencia !== '' && paciente.residencia !== undefined && paciente.residencia !== null) ? paciente.residencia : '-' }
                                                        />
                                                    </div>
                                                    }
                                                </div>
                                            </>}
                                            header={<div className={styles.subHeader}>Otros</div>}
                                            id='pacienteViewOtrosAcordeon'
                                        />
                                    </div>
                                </div>
                            </div>
                            {(paciente.tutores.length > 0) &&
                            <div className={styles.panelSection}>
                                <div className="row">
                                    <div className="col">
                                        <Acordion
                                            body={<>
                                                { paciente.tutores.map(tutor => 
                                                    (<div key={tutor._id}>
                                                        <div className={"row " + styles.tutorDivider}>
                                                            {(configuracionDatosGenerales.tutores.parentesco) &&
                                                            <div className="col-md-6 col-lg-3">
                                                                <TextInputPreview
                                                                    label="Parentesco"
                                                                    name="parentesco"
                                                                    value={ (typeof tutor.parentesco !== 'undefined') ? tutor.parentesco : '' }
                                                                />
                                                            </div>
                                                            }
                                                            {(configuracionDatosGenerales.tutores.nombre) &&
                                                            <div className="col-md-6 col-lg-3">
                                                                <TextInputPreview
                                                                    label="Nombre"
                                                                    name="nombre"
                                                                    value={ (typeof tutor.nombre !== 'undefined') ? tutor.nombre : '' }
                                                                />
                                                            </div>
                                                            }
                                                            {(configuracionDatosGenerales.tutores.email) &&
                                                            <div className="col-md-6 col-lg-3">
                                                                <TextInputPreview
                                                                    label="Correo electrónico"
                                                                    name="email"
                                                                    value={ (typeof tutor.email !== 'undefined') ? tutor.email : '' }
                                                                />
                                                            </div>
                                                            }
                                                            {(configuracionDatosGenerales.tutores.celular) &&
                                                            <div className="col-md-6 col-lg-3">
                                                                <TextInputPreview
                                                                    label="Teléfono móvil"
                                                                    name="celular"
                                                                    value={ (typeof tutor.celular !== 'undefined') ? tutor.celular : '' }
                                                                />
                                                            </div>
                                                            }
                                                        </div>
                                                    </div>)
                                                ) }
                                            </>}
                                            header={<div className={styles.subHeader}>Tutores</div>}
                                            id='pacienteViewTutoresAcordeon'
                                        />
                                    </div>
                                </div>
                            </div>
                            }
                            </>
                            }
                        </div>
                    </div>
                    {(permisos.pacienteUpdate) &&
                    <ModalContainer
                        state={ statusIndependizarPacienteModal }
                        hide={ hideCancelarIndependizar }
                        content={ <IndependizarPacienteModal hide={ hideCancelarIndependizar } asociado={pacienteAsociado} principal={paciente.usuario} anterior={paciente._id} /> }
                    />
                    }
                </>
            }
            </>
        }
        </>
    ) : <Forbidden />
}
